import React from 'react';

import { isNotNil } from 'es-toolkit';

import { Nullable } from '@clearverify/types';

import { DataDisputeWhere, QueryMode, useDataDisputeReportListQuery } from 'src/api';
import { useFilterStorage, useSearchQueryVariables } from 'src/hooks/common';
import { gql } from 'src/utils/gql';
import { getUserFullNameFilter } from 'src/utils/user';

export const useDataDisputeReportList = ({
  filter,
  skip,
  prepareSearchFilterCb = prepareSearchFilter,
}: UseDataDisputeReportListParams = {}) => {
  const searchQueryVariables = useSearchQueryVariables(prepareSearchFilterCb);
  const { sortQueryVariable, paginationQueryVariables } = useFilterStorage();

  const queryVariables = React.useMemo(
    () => ({
      filter: isNotNil(filter)
        ? gql.wrapToAND([filter, searchQueryVariables])
        : searchQueryVariables,
      ...paginationQueryVariables,
      orderBy: sortQueryVariable,
    }),
    [filter, paginationQueryVariables, searchQueryVariables, sortQueryVariable],
  );

  const { data, loading } = useDataDisputeReportListQuery({
    variables: queryVariables,
    skip,
  });

  return {
    dataDisputeReportListCount: data?.dataDisputeCount || 0,
    dataDisputeReportList: data?.dataDisputeList ?? [],
    loading,
  };
};

const prepareSearchFilter = (searchValue: Nullable<string>): DataDisputeWhere =>
  searchValue && searchValue.length > 0
    ? {
        OR: [
          {
            employee: {
              user: getUserFullNameFilter(searchValue),
            },
          },
          {
            dataDisputeItems: {
              some: {
                employer: {
                  displayName: {
                    contains: searchValue,
                    mode: QueryMode.Insensitive,
                  },
                },
              },
            },
          },
        ],
      }
    : {};

type UseDataDisputeReportListParams = {
  filter?: DataDisputeWhere;
  skip?: boolean;
  prepareSearchFilterCb?: (searchValue: Nullable<string>) => DataDisputeWhere;
};
